import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  ButtonBase,
  FormControl,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import { getCutOffDates } from "helpers/helpers";
import { useActions } from "hooks/useActions";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const MaterialDropdown = () => {
  const { t } = useTranslation();
  const { register, getValues, setValue } = useFormContext();
  const { setDeliveryDates } = useActions();

  const currentOption = useSelector((state) => state.orderIntake.currentOption);

  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverWidth, setPopoverWidth] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? "material-popover" : undefined;
  const handleClose = () => setAnchorEl(null);
  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
    setPopoverWidth(e.currentTarget.offsetWidth);
  };

  const popoverContentProps = {
    popoverWidth,
    handleClose,
  };

  const { materials } = getValues();
  useEffect(() => {
    // console.log("setDateByMaterial effect");

    const setDateByMaterial = (materialStr) => {
      const materialDates = currentOption.options
        .filter(({ materials }) =>
          materials.some(
            ({ materialDescription }) => materialDescription === materialStr
          )
        )
        .map((site) => getCutOffDates(site))
        .flat();

      setDeliveryDates(materialDates);
    };

    if (materials?.materialDescription) {
      setDateByMaterial(materials?.materialDescription);
    }
  }, [
    currentOption.options,
    materials?.materialDescription,
    setDeliveryDates,
    setValue,
  ]);

  return (
    <Grid item sx={{ mb: 2 }}>
      <ButtonBase
        onClick={handleClick}
        sx={{
          display: "block",
          width: "100%",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: (theme) => theme.palette.inputBackgroundColor.main,
            border: (theme) =>
              `1px solid ${theme.palette.inputBorderColor.main}`,
            padding: "5px",
            paddingBottom: "8px",
            paddingLeft: "12px",
            "&:hover": {
              background: "#E7E7E7",
            },
          }}
        >
          <Box maxWidth="90%" flexGrow={1}>
            <Stack textAlign="left">
              <TextField
                id="oi-delivery-form-material"
                onMouseDown={(e) => e.preventDefault()}
                label={t("orderIntake.material")}
                variant="filled"
                InputProps={{
                  ...register("materials.materialDescription"),
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  input: { cursor: "pointer" },
                  ".MuiInputBase-root": {
                    cursor: "pointer",
                  },
                  "& .MuiInputBase-root": {
                    background: "none",
                    border: "none",
                  },
                }}
              />
            </Stack>
          </Box>
          <Box>
            <ArrowDropDownIcon />
          </Box>
        </Paper>
      </ButtonBase>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{ marginTop: 1 }}
      >
        <PopoverContent {...popoverContentProps} />
      </Popover>
    </Grid>
  );
};

const PopoverContent = ({ popoverWidth, handleClose }) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const currentOption = useSelector((state) => state.orderIntake.currentOption);

  const [searchInput, setSearchInput] = useState("");
  const handleSearchInput = (e) => setSearchInput(e.target.value);

  return (
    <Paper sx={{ width: popoverWidth, pl: 1, pt: 1 }} aria-hidden={false}>
      <FormControl fullWidth>
        <TextField
          value={searchInput}
          onChange={handleSearchInput}
          label={t("dropdown.searchList")}
          InputLabelProps={{ shrink: true }}
          variant="standard"
          id="site-search"
          aria-describedby="search site input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              "&.MuiInputBase-root": {
                backgroundColor: "white",
                borderColor: "white",
                mr: 1,
              },
            },
          }}
        />
      </FormControl>
      <List
        sx={{
          paddingBottom: 0,
          maxHeight: "250px",
          overflowY: "auto",
        }}
      >
        {currentOption.materials
          .filter((option) => {
            const inputArray = searchInput.toLowerCase().split(" ");
            const optionString = `${option.materialDescription}`.toLowerCase();
            return inputArray.every((item) => optionString.includes(item));
          })
          .map((option, index, options) => (
            <ListItem
              key={index}
              disablePadding
              divider={index < options.length - 1}
              sx={{
                alignItems: "flex-start",
              }}
            >
              <ListItemButton
                onClick={() => {
                  setValue("materials", option, { shouldDirty: true });
                  setValue("edit", false);
                  handleClose();
                }}
                sx={{
                  alignItems: "flex-start",
                  pl: "5px",
                }}
              >
                <Box>
                  <Typography>{option.materialDescription}</Typography>
                  {option.caption && (
                    <Typography display="block" variant="caption">
                      {option.caption}
                    </Typography>
                  )}
                  {option.stringId && (
                    <Typography display="block" variant="caption">
                      {option.stringId}
                    </Typography>
                  )}
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
      </List>
    </Paper>
  );
};

export default MaterialDropdown;
